import { useForm } from "react-hook-form";
import { useState, useEffect, useCallback, useMemo } from "react";
import {
  StyledInput,
  StyledInputContainer,
  StyledInputGroup,
  StyledLabel,
  StyledSelectGroup,
  StyledSelectContainer,
  StyledSelect,
} from "../../StyledCss/styledInputs";
import { PrimaryButton, SecondaryButton } from "../../StyledCss/styledbtn";
import {
  getProjectModules,
} from "../../api/services/hroneApi";
import { convertKeysToCamelCase } from "../../util/convertCamelCase";
import { ErrorText } from "../../StyledCss/styledComponents";
const AddWorkLogPopup = ({
  onSubmitData,
  closeBtn,
  defaultValues,
  isEditMode,
  id,
  selectedProjectId,
  isLoading,
  errorMsg,
  onClearError,
  projectsData
}) => {
  // const [projectsData, setProjectsData] = useState([]);
  const [modulesData, setModulesData] = useState([]);
  const [maxDate, setMaxDate] = useState("");

  const {
    register,
    handleSubmit,
    watch,
    setValue,
    formState: { errors },
  } = useForm({
    defaultValues: {
      attendance_type: "WORKING_DAY",
      ...defaultValues,
    },
  });

  const fetchModules = useCallback(
    async (projectId) => {
      try {
        const ModulesResponse = await getProjectModules(projectId);

        if (ModulesResponse.ok) {
          const modulesData = await ModulesResponse.json();
          setModulesData(modulesData.map(convertKeysToCamelCase));
        }
      } catch (error) {
        console.error("Error fetching modules:", error);
      }
    },
    [],
  );


  const calculateLastWorkingDay = useCallback(async () => {
    try {
      const now = new Date();
      const year = now.getFullYear();
      const month = now.getMonth();
      const lastDay = new Date(year, month + 1, 0);
      
      let lastWorkingDay = lastDay.getDate();
      
      for (let day = lastDay.getDate(); day >= 1; day--) {
        const checkDate = new Date(year, month, day);
        const dayOfWeek = checkDate.getDay();
        
        if (dayOfWeek !== 0 && dayOfWeek !== 6) {
          lastWorkingDay = day;
          break;
        }
      }
      
      const maxDateStr = `${year}-${String(month + 1).padStart(2, '0')}-${String(lastWorkingDay).padStart(2, '0')}`;
      setMaxDate(maxDateStr);
    } catch (error) {
      console.error("Error calculating last working day:", error);
    }
  }, []);

  useEffect(() => {
    calculateLastWorkingDay();
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    if (isEditMode && defaultValues?.projectId) {
      setValue("projectId", defaultValues.projectId);
      fetchModules(defaultValues.projectId);
    } else if (!isEditMode && selectedProjectId) {
      setValue("projectId", selectedProjectId);
      fetchModules(selectedProjectId);
    }
  },
  // eslint-disable-next-line 
  [projectsData, isEditMode, defaultValues?.projectId, selectedProjectId]);

  useEffect(() => {
    if (isEditMode && defaultValues?.moduleId && modulesData.length > 0) {
      setValue("moduleId", defaultValues.moduleId);
    }
  },
  // eslint-disable-next-line
   [modulesData, isEditMode, defaultValues?.moduleId]);

  const attendanceType = watch("attendanceType");
  const projectId = watch("projectId") || selectedProjectId;

  const minDate = projectsData.find((p) => p.projectId === projectId)?.startDate ?? "";

  const isLeave = attendanceType === "LEAVE";
  const isPublicHoliday = attendanceType === "PUBLIC_HOLIDAY";
  const isFixedBilling = projectsData.find((p) => p.projectId === projectId)?.billingType === "FIXED";
  const shouldHideInputs = isLeave || isPublicHoliday;
  const shouldHideHours = shouldHideInputs || isFixedBilling;

  const onSubmit = 
    (data) => {
      if (shouldHideInputs) {
        data.hours = 0;
        data.dayPlan = "";
      } else if (isFixedBilling) {
        data.hours = 8;
      }
      onSubmitData(data);
    }


  const hoursInputStyle = useMemo(
    () => ({
      ...(errors.hours && { borderLeft: "10px solid red" }),
      ...(shouldHideHours && {
        backgroundColor: "#f0f0f0",
        cursor: "not-allowed",
      }),
    }),
    [errors.hours, shouldHideHours],
  );

  const workDescriptionStyle = useMemo(
    () => ({
      height: "200px",
      minHeight: "120px",
      overflowY: "auto",
      resize: "none",
      padding: "12px",
      width: "650px",
      fontSize: "14px",
      lineHeight: "1.5",
      fontFamily: "inherit",
      ...(errors.dayPlan && { borderLeft: "10px solid red" }),
    }),
    [errors.dayPlan],
  );

  const hoursValidation = useMemo(
    () => ({
      required: !shouldHideHours,
      validate: (value) =>
        shouldHideHours || (!isNaN(value) && Number(value) > 0 && Number(value) <= 24),
    }),
    [shouldHideHours],
  );

  return (
    <div className="popup-content">
      <div autoComplete="off" className="add-employee-form">
        <div className="add-employee-form-container">
          <StyledSelectGroup>
            <StyledSelectContainer>
              <StyledSelect
                disabled={isEditMode}
                style={errors.projectId && { borderLeft: "10px solid red" }}
                {...register("projectId", { 
                  required: true,
                  onChange: (e) => {
                    if (e.target.value) {
                      fetchModules(e.target.value);
                    } else {
                      setModulesData([]);
                      setValue("moduleId", "");
                    }
                  }
                })}
              >
                <option value="">---Select Project Name---</option>
                {projectsData.map((project) => (
                  <option key={project.projectId} value={project.projectId}>
                    {project.projectName}
                  </option>
                ))}
              </StyledSelect>
              <StyledLabel className="user-label"hasValue={isEditMode}>Project Name*</StyledLabel>
            </StyledSelectContainer>
          </StyledSelectGroup>

          <StyledSelectGroup>
            <StyledSelectContainer>
              <StyledSelect
                style={errors.moduleId && { borderLeft: "10px solid red" }}
                {...register("moduleId")}
              >
                <option value="">---Select Module Name---</option>
                {modulesData.map((module) => (
                  <option key={module.id} value={module.id}>
                    {module.name}
                  </option>
                ))}
              </StyledSelect>
              <StyledLabel className="user-label">Module Name</StyledLabel>
            </StyledSelectContainer>
          </StyledSelectGroup>

          {/* Date */}
          <StyledInputGroup>
            <StyledInputContainer>
              <StyledInput
                type="date"
                style={errors.workDate && { borderLeft: "10px solid red" }}
                {...register("workDate", {
                  required: true,
                  onChange: () => onClearError?.(),
                  validate: (value) =>
                    !maxDate || value <= maxDate || "Future dates not allowed",
                })}
                min={minDate}
                max={maxDate}
              />
              <StyledLabel className="user-label">Date*</StyledLabel>
            </StyledInputContainer>
            {errors.workDate?.message && (
              <ErrorText className="mt-2">*{errors.workDate.message}</ErrorText>
            )}
          </StyledInputGroup>

          {/* Hours */}
          {!shouldHideHours && (
            <StyledInputGroup>
              <StyledInputContainer>
                <StyledInput
                  type="number"
                  placeholder="Enter hours"
                  min ="0"
                  step="0.5"
                  style={hoursInputStyle}
                  onKeyDown={(e) => {
                    if (['-', '+', 'e', 'E'].includes(e.key)) e.preventDefault();
                    if (e.key === '.' && e.target.value === '') e.preventDefault();
                  }}
                  {...register("hours", hoursValidation)}
                />
                <StyledLabel className="user-label">Hours*</StyledLabel>
              </StyledInputContainer>
            </StyledInputGroup>
          )}

          {/* Attendance Type */}
          <StyledSelectGroup>
            <StyledSelectContainer>
              <StyledSelect
                style={
                  errors.attendance_type && { borderLeft: "10px solid red" }
                }
                {...register("attendanceType", { required: true })}
              >
                <option value="WORKING_DAY">Working Day</option>
                <option value="LEAVE">Leave</option>
                <option value="PUBLIC_HOLIDAY">Public Holiday</option>
              </StyledSelect>
              <StyledLabel className="user-label">Attendance Type*</StyledLabel>
            </StyledSelectContainer>
          </StyledSelectGroup>

          {/* Work Description */}
          {!shouldHideInputs && (
            <StyledInputGroup
              style={{
                flex: "1 1 100%",
                width: "100%",
                flexBasis: "100%",
                order: 999,
              }}
            >
              <StyledInputContainer>
                <StyledInput
                  as="textarea"
                  placeholder="Describe your work in detail..."
                  style={workDescriptionStyle}
                  {...register("dayPlan", {
                    required: !shouldHideInputs,
                    validate: (value) =>
                      shouldHideInputs || value.trim().length > 0,
                    setValueAs: (value) => value?.trim(),
                  })}
                />
                <StyledLabel className="user-label">
                  Work Description*
                </StyledLabel>
              </StyledInputContainer>
            </StyledInputGroup>
          )}
        </div>
         {errorMsg && (
              <ErrorText className="mt-3">*{errorMsg}</ErrorText>
                  )}

        {/* Buttons */}
        <div className="d-flex gap-2 justify-content-end mt-5">
          <SecondaryButton type="button" onClick={closeBtn}>
            Cancel
          </SecondaryButton>
          <PrimaryButton type="button" disabled={errors.workDate} onClick={handleSubmit(onSubmit)}>
            {isLoading
              ? isEditMode
                ? "Updating..."
                : "Adding..."
              : isEditMode
              ? "Update"
              : "Add"}
          </PrimaryButton>
        </div>
      </div>
    </div>
  );
};

export default AddWorkLogPopup;
